import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const GRADE_LABELS = ["F", "E", "D", "C", "B", "A"];

/**
 * Export results to Excel with separate Score and Grade columns per course.
 *
 * @param {Array}  students       - Array of student result objects
 * @param {Array}  courseCodes    - Ordered array of course codes (columns)
 * @param {string} semester       - "1" or "2"
 * @param {string} filename       - Output filename (no extension)
 */
export function exportResultsToExcel(
  students,
  courseCodes,
  semester,
  filename,
) {
  if (!students?.length) return;

  const isSem2 = String(semester) === "2";

  // ── Build header row ────────────────────────────────────────────────────────
  const header = ["S/N", "Names", "Reg. No."];
  courseCodes.forEach((code) => {
    header.push(`${code.toUpperCase()} Score`);
    header.push(`${code.toUpperCase()} Grade`);
  });
  header.push("GPA");
  if (isSem2) {
    header.push("Session CGPA");
    header.push("Overall CGPA");
  }

  // ── Build data rows ─────────────────────────────────────────────────────────
  const rows = students.map((student, i) => {
    const row = [i + 1, student.fullname || "", student.reg_no || ""];

    courseCodes.forEach((code) => {
      const course = (student.courses || []).find(
        (c) => c.course_code?.toLowerCase() === code.toLowerCase(),
      );
      if (course) {
        row.push(Number(course.total ?? 0).toFixed(0));
        row.push(GRADE_LABELS[course.grade] ?? "");
      } else {
        row.push("");
        row.push("");
      }
    });

    const gpa = Number(student.gpa);
    row.push(Number.isFinite(gpa) ? gpa.toFixed(2) : "");

    if (isSem2) {
      const sessionGpa = Number(student.session_gpa);
      const cgpa = Number(student.cgpa);
      row.push(Number.isFinite(sessionGpa) ? sessionGpa.toFixed(2) : "");
      row.push(Number.isFinite(cgpa) ? cgpa.toFixed(2) : "");
    }

    return row;
  });

  // ── Build worksheet ─────────────────────────────────────────────────────────
  const wsData = [header, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Set column widths
  ws["!cols"] = [
    { wch: 5 }, // S/N
    { wch: 28 }, // Names
    { wch: 16 }, // Reg. No.
    ...courseCodes.flatMap(() => [{ wch: 9 }, { wch: 8 }]), // Score + Grade per course
    { wch: 7 }, // GPA
    ...(isSem2 ? [{ wch: 13 }, { wch: 13 }] : []),
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Results");

  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], { type: "application/octet-stream" });
  saveAs(blob, `${filename}.xlsx`);
}
